import AnalyticsIcon from "@mui/icons-material/Analytics";
import BarChartIcon from "@mui/icons-material/BarChart";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import GroupIcon from "@mui/icons-material/Group";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import MenuIcon from "@mui/icons-material/Menu";
import PageViewIcon from "@mui/icons-material/Pageview";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import WorkIcon from "@mui/icons-material/Work";
import AppBar from "@mui/material/AppBar";
import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import type React from "react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../../features/authContext";
import { signOut } from "../../services/authTokens";
import { useAdminStatus } from "../../sharedHooks/useAdminStatus";

interface RouteItem {
	path: string;
	label: string;
	icon: React.ReactNode;
	adminOnly: boolean;
}

const routes: RouteItem[] = [
	{
		path: "/overview",
		label: "Overview",
		icon: <PageViewIcon />,
		adminOnly: false,
	},
	{
		path: "/live-dashboard",
		label: "Live Dash",
		icon: <LiveTvIcon />,
		adminOnly: false,
	},
	{
		path: "/man-client-analytics",
		label: "Dating",
		icon: <AnalyticsIcon />,
		adminOnly: false,
	},
	{
		path: "/finances",
		label: "Finances",
		icon: <PriceChangeOutlinedIcon />,
		adminOnly: true,
	},
	{
		path: "/clients",
		label: "Clients",
		icon: <GroupIcon />,
		adminOnly: false,
	},
	{
		path: "/translators",
		label: "Translators",
		icon: <WorkIcon />,
		adminOnly: false,
	},
	{
		path: "/chart",
		label: "Charts",
		icon: <BarChartIcon />,
		adminOnly: false,
	},
	{
		path: "/schedule",
		label: "Schedule",
		icon: <ScheduleIcon />,
		adminOnly: false,
	},
	{
		path: "/business-admins",
		label: "Admins",
		icon: <SupervisorAccountIcon />,
		adminOnly: true,
	},
];

interface HeaderNavigationItemProps {
	label: string;
	icon: React.ReactNode;
	to: string;
	onClick?: () => void;
}

const HeaderNavigationItem: React.FC<HeaderNavigationItemProps> = ({
	label,
	icon,
	to,
	onClick,
}) => {
	const navigate = useNavigate();
	return (
		<ListItemButton onClick={() => (onClick ? onClick() : navigate(to))}>
			<ListItemIcon>{icon}</ListItemIcon>
			<ListItemText primary={label} />
		</ListItemButton>
	);
};

interface NavigationProps {
	onToggleTheme: () => void;
}

export default function Navigation({ onToggleTheme }: NavigationProps) {
	const { user, clearUser } = useAuth();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const activeTab =
		routes.find((r) => r.path !== "/" && pathname.startsWith(r.path))?.path ??
		pathname;
	const isDesktop = useMediaQuery("(min-width:840px)");
	const [state, setState] = useState({ top: false });
	const isAdmin = useAdminStatus();

	const toggleDrawer =
		(anchor: string, open: boolean) =>
		(event: React.KeyboardEvent | React.MouseEvent) => {
			if (
				event &&
				event.type === "keydown" &&
				((event as React.KeyboardEvent).key === "Tab" ||
					(event as React.KeyboardEvent).key === "Shift")
			) {
				return;
			}
			setState({ ...state, [anchor]: open });
		};

	const onLogOut = () => {
		signOut();
		clearUser();
	};

	const list = (anchor: string) => (
		<Box
			role="presentation"
			onClick={toggleDrawer(anchor, false)}
			onKeyDown={toggleDrawer(anchor, false)}
			sx={{ width: anchor === "top" ? "auto" : 250 }}
		>
			<List>
				{routes.map(
					(route) =>
						(!route.adminOnly || isAdmin) && (
							<HeaderNavigationItem
								key={route.path}
								label={route.label}
								icon={route.icon}
								to={route.path}
							/>
						),
				)}
				<ListItemButton onClick={onToggleTheme}>
					<ListItemIcon>
						<DarkModeIcon />
					</ListItemIcon>
					<ListItemText primary={"Toggle theme"} />
				</ListItemButton>
				{user ? (
					<ListItemButton onClick={onLogOut}>
						<ListItemIcon>
							<ExitToAppIcon />
						</ListItemIcon>
						<ListItemText primary={"Log out"} />
					</ListItemButton>
				) : null}
			</List>
		</Box>
	);

	if (!user) {
		return null;
	}

	return (
		<AppBar
			position="sticky"
			color="default"
			sx={{
				backgroundColor: (theme) =>
					theme.palette.mode === "dark"
						? "rgba(11,20,18,0.72)"
						: "rgba(255,255,255,0.46)",
				backdropFilter: "saturate(140%) blur(12px)",
				WebkitBackdropFilter: "saturate(140%) blur(12px)",
				borderBottom: "1px solid",
				borderColor: "divider",
			}}
		>
			<Toolbar
				sx={{ gap: 2, maxWidth: 1240, mx: "auto", width: "100%", py: 0.75 }}
			>
				{!isDesktop && (
					<IconButton
						edge="start"
						aria-label="Open navigation"
						onClick={toggleDrawer("top", true) as React.MouseEventHandler}
					>
						<MenuIcon />
					</IconButton>
				)}
				<Typography
					variant="h6"
					sx={{ display: { xs: "block", md: "none" }, mr: 2 }}
				>
					Navigation
				</Typography>
				{isDesktop && (
					<BottomNavigation
						showLabels
						value={activeTab}
						onChange={(_event, newValue) => {
							navigate(newValue);
						}}
						sx={{ flexGrow: 1, bgcolor: "transparent", py: 0.25, gap: 0.75 }}
					>
						{routes.map(
							(route) =>
								(!route.adminOnly || isAdmin) && (
									<BottomNavigationAction
										key={route.path}
										label={route.label}
										icon={route.icon}
										value={route.path}
										sx={{
											color: "text.secondary",
											py: 1,
											minHeight: 48,
											borderRadius: 1,
											mx: 0.25,
											"&:hover": {
												color: "text.primary",
												backgroundColor: "action.hover",
											},
											"&.Mui-selected": {
												color: "primary.main",
												backgroundColor: (theme) =>
													theme.palette.mode === "dark"
														? "rgba(52,211,153,0.18)"
														: "rgba(16,185,129,0.14)",
												boxShadow: (theme) =>
													theme.palette.mode === "dark"
														? "0 6px 16px rgba(0,0,0,0.20)"
														: "0 6px 16px rgba(16,185,129,0.18)",
											},
											"& .MuiBottomNavigationAction-label": {
												fontSize: 11,
												fontWeight: 600,
											},
											"&.Mui-selected .MuiBottomNavigationAction-label": {
												fontWeight: 700,
											},
										}}
									/>
								),
						)}
					</BottomNavigation>
				)}
				{user && isDesktop && (
					<Button
						color="inherit"
						startIcon={<ExitToAppIcon />}
						onClick={onLogOut}
					>
						Log out
					</Button>
				)}
				{isDesktop && (
					<IconButton
						color="inherit"
						onClick={onToggleTheme}
						sx={{ ml: 1, "&:hover": { color: "primary.main" } }}
					>
						<DarkModeIcon />
					</IconButton>
				)}
				{!isDesktop && (
					<IconButton
						color="inherit"
						onClick={onToggleTheme}
						sx={{ ml: "auto", "&:hover": { color: "primary.main" } }}
					>
						<DarkModeIcon />
					</IconButton>
				)}
			</Toolbar>
			<Drawer
				anchor="top"
				open={state.top}
				onClose={toggleDrawer("top", false) as React.MouseEventHandler}
				slotProps={{
					paper: {
						sx: {
							backgroundColor: "background.paper",
							backdropFilter: "saturate(140%) blur(14px)",
							WebkitBackdropFilter: "saturate(140%) blur(14px)",
							borderBottom: "1px solid",
							borderColor: "divider",
							borderTopLeftRadius: 0,
							borderTopRightRadius: 0,
						},
					},
				}}
			>
				{list("top")}
			</Drawer>
		</AppBar>
	);
}
